/**
 * Billing data (features.billing). The office reads dockets to invoice, invoices, payments and payment claims
 * under RLS; pricing, numbering, PDFs, sending and recording go through the API's /admin routes. Bank details
 * are read and written by directors directly under RLS (billing_settings).
 */
import { adminBlob, adminFetch } from "../../lib/api";
import { localYmd } from "../../lib/format";
import { PREVIEW } from "../../lib/preview";
import { supabase } from "../../lib/supabase";

const previewOnly = (): never => {
  throw new Error("Not available in preview mode.");
};
const check = <T>(r: { data: T | null; error: { message: string } | null }): T => {
  if (r.error) throw new Error(r.error.message);
  return r.data as T;
};

export type InvoiceStatus = "issued" | "sent" | "part_paid" | "paid" | "disputed" | "void";
export const INVOICE_STATUS_LABEL: Record<InvoiceStatus, string> = { issued: "Not sent", sent: "Sent", part_paid: "Part paid", paid: "Paid", disputed: "Disputed", void: "Void" };

export interface ToInvoice {
  id: string; number: string; shiftDate: string; status: string; signerName: string | null;
  job: { id: string; reference: string; title: string; customerId: string | null; customerName: string | null; poRequired: boolean };
}
export interface PricedLine { kind: string; description: string; quantity: number; unit: string; unitPrice: number; amount: number; gst: number }
export interface InvoicePreview {
  lines: PricedLine[]; subtotal: number; gst: number; total: number; warnings: string[];
  dockets: { id: string; number: string; shiftDate: string; status: string }[]; skippedExtras: { id: string; description: string }[];
  period: { from: string; to: string }; ratesFrom: string;
}
export interface InvoiceRow {
  id: string; number: string; customer_id: string; job_id: string | null; issue_date: string; due_date: string; po_number: string | null;
  subtotal: number; gst: number; total: number; amount_paid: number; status: InvoiceStatus; is_payment_claim: boolean; sent_at: string | null;
  sent_to: string[] | null; dispute_note: string | null; void_reason: string | null; xero_invoice_id: string | null; reminder_sent_at: string | null;
  snapshot: { customer: { legalName: string; email: string | null }; job: { reference: string; title: string; site: string | null } | null; period: { from: string; to: string } | null; dockets: string[]; ratesFrom: string };
  created_at: string;
}
export interface ClaimRow {
  id: string; customer_id: string; kind: "invoice" | "statement"; invoice_id: string | null; invoice_ids: string[]; claim_month: string; claimed_amount: number;
  construction_work: string; served_at: string | null; served_to: string[] | null; schedule_due_date: string | null; created_at: string;
  payment_schedules: { id: string; scheduled_amount: number; reasons: string | null; received_on: string; file_name: string | null }[] | null;
}
export interface Payment { id: string; amount: number; paid_on: string; source: "office" | "xero"; reference: string | null; recorded_at: string }

const INVOICE_COLS = "id, number, customer_id, job_id, issue_date, due_date, po_number, subtotal, gst, total, amount_paid, status, is_payment_claim, sent_at, sent_to, dispute_note, void_reason, xero_invoice_id, reminder_sent_at, snapshot, created_at";
const num = (v: unknown) => Number(v ?? 0);
const toInvoice = (r: Record<string, unknown>): InvoiceRow => ({ ...(r as unknown as InvoiceRow), subtotal: num(r.subtotal), gst: num(r.gst), total: num(r.total), amount_paid: num(r.amount_paid) });
const toClaim = (r: Record<string, unknown>): ClaimRow => {
  // payment_schedules.claim_id is unique, so PostgREST may return one object rather than a list.
  const s = r.payment_schedules as Record<string, unknown>[] | Record<string, unknown> | null;
  const list = (Array.isArray(s) ? s : s ? [s] : []).map((x) => ({ ...(x as unknown as NonNullable<ClaimRow["payment_schedules"]>[number]), scheduled_amount: num(x.scheduled_amount) }));
  return { ...(r as unknown as ClaimRow), claimed_amount: num(r.claimed_amount), payment_schedules: list };
};
export const owing = (i: Pick<InvoiceRow, "total" | "amount_paid">) => Math.round((i.total - i.amount_paid) * 100) / 100;
export const isOpen = (i: Pick<InvoiceRow, "status">) => ["issued", "sent", "part_paid", "disputed"].includes(i.status);

/* ---------- reads (RLS) ---------- */

/** Signed dockets not yet invoiced (not superseded by a correction), with their job and client. */
export async function docketsToInvoice(): Promise<ToInvoice[]> {
  if (PREVIEW) return [];
  const rows = check(
    await supabase
      .from("dockets")
      .select("id, number, shift_date, status, signer_name, jobs!inner(id, reference, title, customer_id, customers(name, legal_name, po_required))")
      .is("invoice_id", null)
      .is("superseded_by", null)
      .in("status", ["signed", "disputed", "paper", "corrected"])
      .order("shift_date"),
  ) as unknown as Record<string, unknown>[];
  return rows.map((r) => {
    const j = r.jobs as { id: string; reference: string; title: string; customer_id: string | null; customers: { name: string; legal_name: string | null; po_required: boolean } | null };
    return {
      id: r.id as string, number: r.number as string, shiftDate: r.shift_date as string, status: r.status as string, signerName: (r.signer_name as string | null) ?? null,
      job: { id: j.id, reference: j.reference, title: j.title, customerId: j.customer_id, customerName: j.customers ? j.customers.legal_name ?? j.customers.name : null, poRequired: Boolean(j.customers?.po_required) },
    };
  });
}

export async function listInvoices(): Promise<InvoiceRow[]> {
  if (PREVIEW) return [];
  return (check(await supabase.from("invoices").select(INVOICE_COLS).order("created_at", { ascending: false }).limit(500)) as Record<string, unknown>[]).map(toInvoice);
}

export async function getInvoice(id: string) {
  if (PREVIEW) return previewOnly();
  const [inv, lines, pays, claim] = await Promise.all([
    supabase.from("invoices").select(INVOICE_COLS).eq("id", id).maybeSingle(),
    supabase.from("invoice_lines").select("kind, description, quantity, unit, unit_price, amount, gst, sort_order").eq("invoice_id", id).order("sort_order"),
    supabase.from("invoice_payments").select("id, amount, paid_on, source, reference, recorded_at").eq("invoice_id", id).order("paid_on"),
    supabase.from("payment_claims").select("*, payment_schedules(id, scheduled_amount, reasons, received_on, file_name)").eq("invoice_id", id).maybeSingle(),
  ]);
  const row = check(inv) as Record<string, unknown> | null;
  if (!row) return null;
  return {
    invoice: toInvoice(row),
    lines: (check(lines) as Record<string, unknown>[]).map((l) => ({ kind: l.kind as string, description: l.description as string, quantity: num(l.quantity), unit: l.unit as string, unitPrice: num(l.unit_price), amount: num(l.amount), gst: num(l.gst) })),
    payments: (check(pays) as Record<string, unknown>[]).map((p) => ({ ...(p as unknown as Payment), amount: num(p.amount) })),
    claim: (check(claim) as Record<string, unknown> | null) ? toClaim(check(claim) as Record<string, unknown>) : null,
  };
}

export async function listClaims(): Promise<ClaimRow[]> {
  if (PREVIEW) return [];
  return (check(await supabase.from("payment_claims").select("*, payment_schedules(id, scheduled_amount, reasons, received_on, file_name)").order("created_at", { ascending: false }).limit(200)) as Record<string, unknown>[]).map(toClaim);
}

export async function customerNames(ids: string[]): Promise<Record<string, string>> {
  if (PREVIEW || !ids.length) return {};
  const rows = check(await supabase.from("customers").select("id, name, legal_name").in("id", [...new Set(ids)])) as { id: string; name: string; legal_name: string | null }[];
  return Object.fromEntries(rows.map((r) => [r.id, r.legal_name ?? r.name]));
}

/** Customers with unpaid invoices, for a monthly statement. */
export async function customersWithUnpaid(): Promise<{ id: string; name: string }[]> {
  const open = (await listInvoices()).filter(isOpen);
  const names = await customerNames(open.map((i) => i.customer_id));
  return Object.entries(names).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
}

/* ---------- writes (API) ---------- */

export const previewInvoice = (docketIds: string[]) => (PREVIEW ? previewOnly() : adminFetch<InvoicePreview>("/invoices/preview", { method: "POST", body: JSON.stringify({ docketIds }) }));
export const createInvoice = (docketIds: string[], poNumber: string | null) =>
  PREVIEW ? previewOnly() : adminFetch<{ invoice: { id: string; number: string; total: number; isPaymentClaim: boolean; dueDate: string } }>("/invoices", { method: "POST", body: JSON.stringify({ docketIds, poNumber }) });
export const invoicePdf = (id: string) => (PREVIEW ? previewOnly() : adminBlob(`/invoices/${id}/pdf`, {}, { timeoutMs: 60_000 }));
export const sendInvoice = (id: string, to?: string[]) =>
  PREVIEW ? previewOnly() : adminFetch<{ invoice: { status: string; sentTo: string[] }; xero: { created: boolean; error: string | null } }>(`/invoices/${id}/send`, { method: "POST", body: JSON.stringify(to?.length ? { to } : {}) }, { timeoutMs: 60_000 });
export const recordPayment = (id: string, amount: number, paidOn: string, reference: string | null) =>
  PREVIEW ? previewOnly() : adminFetch(`/invoices/${id}/payments`, { method: "POST", body: JSON.stringify({ amount, paidOn, reference }) });
export const disputeInvoice = (id: string, note: string) => (PREVIEW ? previewOnly() : adminFetch(`/invoices/${id}/dispute`, { method: "POST", body: JSON.stringify({ note }) }));
export const voidInvoice = (id: string, reason: string) => (PREVIEW ? previewOnly() : adminFetch(`/invoices/${id}/void`, { method: "POST", body: JSON.stringify({ reason }) }));
export const makePaymentClaim = (id: string) => (PREVIEW ? previewOnly() : adminFetch(`/invoices/${id}/payment-claim`, { method: "POST" }));
export const createStatement = (customerId: string, month: string) =>
  PREVIEW ? previewOnly() : adminFetch<{ claim: { id: string; claimedAmount: number } }>("/payment-claims/statement", { method: "POST", body: JSON.stringify({ customerId, month }) });
export const sendStatement = (id: string) => (PREVIEW ? previewOnly() : adminFetch<{ claim: { servedAt: string; scheduleDueDate: string } }>(`/payment-claims/${id}/send`, { method: "POST", body: "{}" }, { timeoutMs: 60_000 }));
export const claimPdf = (id: string) => (PREVIEW ? previewOnly() : adminBlob(`/payment-claims/${id}/pdf`, {}, { timeoutMs: 60_000 }));
export const scheduleFile = (id: string) => (PREVIEW ? previewOnly() : adminBlob(`/payment-claims/${id}/schedule/file`));
export function recordSchedule(id: string, s: { scheduledAmount: number; receivedOn: string; reasons: string; file: File | null }) {
  if (PREVIEW) return previewOnly();
  const form = new FormData();
  form.set("scheduledAmount", String(s.scheduledAmount));
  form.set("receivedOn", s.receivedOn);
  form.set("reasons", s.reasons);
  if (s.file) form.set("file", s.file);
  return adminFetch(`/payment-claims/${id}/schedule`, { method: "POST", body: form });
}

/* ---------- bank details (directors, RLS) ---------- */

export interface BankSettings { bank_account_name: string | null; bank_bsb: string | null; bank_account_number: string | null; updated_at: string | null }

export async function myRole(): Promise<string | null> {
  if (PREVIEW) return "director";
  const { data: s } = await supabase.auth.getSession();
  const { data } = await supabase.from("profiles").select("role, organisation_id").eq("id", s.session?.user.id ?? "").maybeSingle();
  return (data?.role as string | undefined) ?? null;
}

export async function getBank(): Promise<BankSettings | null> {
  if (PREVIEW) return null;
  return check(await supabase.from("billing_settings").select("bank_account_name, bank_bsb, bank_account_number, updated_at").maybeSingle()) as BankSettings | null;
}

export async function saveBank(b: { accountName: string; bsb: string; accountNumber: string }) {
  if (PREVIEW) return previewOnly();
  const { data: s } = await supabase.auth.getSession();
  const uid = s.session?.user.id ?? "";
  const { data: p, error: pe } = await supabase.from("profiles").select("organisation_id").eq("id", uid).single();
  if (pe) throw new Error(pe.message);
  const { error } = await supabase
    .from("billing_settings")
    .upsert({ organisation_id: p.organisation_id, bank_account_name: b.accountName, bank_bsb: b.bsb, bank_account_number: b.accountNumber, updated_by: uid }, { onConflict: "organisation_id" });
  if (error) throw new Error(error.code === "42501" ? "Only a director can change the bank details." : error.message);
}

/* ---------- Today ---------- */

export async function billingToday() {
  if (PREVIEW) return { overdue: [] as { customer: string; count: number; owing: number; oldest: string }[], toInvoice: 0, toInvoiceJobs: 0, awaitingSchedule: [] as ClaimRow[], names: {} as Record<string, string> };
  const today = localYmd();
  const [invoices, dockets, claims] = await Promise.all([listInvoices(), docketsToInvoice(), listClaims()]);
  const overdueRows = invoices.filter((i) => isOpen(i) && i.due_date < today);
  const awaiting = claims.filter((c) => c.served_at && !(c.payment_schedules ?? []).length);
  const names = await customerNames([...overdueRows.map((i) => i.customer_id), ...awaiting.map((c) => c.customer_id)]);
  const by = new Map<string, { customer: string; count: number; owing: number; oldest: string }>();
  for (const i of overdueRows) {
    const k = i.customer_id;
    const e = by.get(k) ?? { customer: names[k] ?? i.snapshot.customer.legalName, count: 0, owing: 0, oldest: i.due_date };
    e.count++;
    e.owing = Math.round((e.owing + owing(i)) * 100) / 100;
    if (i.due_date < e.oldest) e.oldest = i.due_date;
    by.set(k, e);
  }
  return { overdue: [...by.values()].sort((a, b) => a.oldest.localeCompare(b.oldest)), toInvoice: dockets.length, toInvoiceJobs: new Set(dockets.map((d) => d.job.id)).size, awaitingSchedule: awaiting, names };
}

/** Units as words, singular and plural, as on the invoice PDF (api/src/jobs/invoice-pdf.ts). */
const UNIT: Record<string, [string, string]> = { "crew-hour": ["crew hour", "crew hours"], shift: ["shift", "shifts"], trip: ["trip", "trips"], each: ["each", "each"], hour: ["hour", "hours"] };
export const unitLabel = (unit: string, quantity: number) => (UNIT[unit] ? UNIT[unit]![quantity === 1 ? 0 : 1] : unit);
